import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, ShieldAlert, Crown, Stethoscope, Wrench, Filter } from "lucide-react";

const COVENANT_FILTERS = [
    { id: "all", label: "All" },
    { id: "true", label: "Covenant signed" },
    { id: "false", label: "Pending covenant" },
];

const ROLE_META = {
    technician: { label: "On-Demand Technicians", Icon: Wrench, accent: "border-cyan-400/30 bg-cyan-400/5" },
    surgeon: { label: "Sovereign Surgeons", Icon: Stethoscope, accent: "border-rose-400/30 bg-rose-400/5" },
    admiral: { label: "Fleet Admirals", Icon: Crown, accent: "border-amber-400/30 bg-amber-400/5" },
};

export default function AdminBoarders() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [covenant, setCovenant] = useState("all");

    useEffect(() => {
        if (authLoading) return;
        if (!user || user.role !== "admin") return;
        let active = true;
        setLoading(true);
        const params = {};
        if (covenant !== "all") params.covenant = covenant;
        api.get("/admin/boarders", { params })
            .then((r) => active && setData(r.data))
            .catch((err) => {
                if (!active) return;
                toast.error(err.response?.data?.detail || "Failed to load queue");
            })
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [authLoading, user, covenant]);

    if (authLoading) {
        return <div className="mx-auto max-w-md px-6 py-24 text-center text-[#A3A3A3]">Loading…</div>;
    }
    if (!user || user.role !== "admin") {
        return (
            <div className="mx-auto max-w-md px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">Director's Queue</div>
                <h1 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA]">Admin only</h1>
                <p className="mt-3 text-[#A3A3A3]">President Kirk's review queue. Restricted.</p>
                <button
                    onClick={() => navigate("/login?next=/admin/boarders")}
                    className="mt-8 rounded-sm px-6 py-3 text-sm font-medium"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    data-testid="boarders-signin"
                >
                    Sign in
                </button>
            </div>
        );
    }

    const buckets = data?.buckets || { technician: [], surgeon: [], admiral: [], unassigned: [] };
    const counts = data?.counts || {};

    return (
        <div data-testid="admin-boarders-page" className="mx-auto max-w-[1400px] px-6 py-16 md:px-10">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <div className="caption text-[var(--brand-accent)]">Director's Queue</div>
                    <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">
                        Boarders Triage
                    </h1>
                    <p className="mt-3 max-w-2xl text-[#A3A3A3]">
                        Categorized new boarders awaiting President Kirk's review. Admirals first.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Filter size={14} className="text-[#737373]" />
                    {COVENANT_FILTERS.map((f) => {
                        const active = covenant === f.id;
                        return (
                            <button
                                key={f.id}
                                data-testid={`boarders-filter-${f.id}`}
                                data-state={active ? "active" : "inactive"}
                                onClick={() => setCovenant(f.id)}
                                className={`rounded-full border px-4 py-1.5 text-xs tracking-wider transition-all ${
                                    active
                                        ? "border-[var(--brand-accent)] bg-[rgba(201,169,110,0.10)] text-[var(--brand-accent)]"
                                        : "border-white/10 text-[#A3A3A3] hover:border-white/30 hover:text-[#FAFAFA]"
                                }`}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="py-24 text-center text-[#A3A3A3]">Loading queue…</div>
            ) : (
                <div className="grid gap-6 lg:grid-cols-3">
                    <Column roleKey="admiral" rows={buckets.admiral} count={counts.admiral} />
                    <Column roleKey="surgeon" rows={buckets.surgeon} count={counts.surgeon} />
                    <Column roleKey="technician" rows={buckets.technician} count={counts.technician} />
                </div>
            )}

            {!loading && (buckets.unassigned || []).length > 0 && (
                <div className="mt-12">
                    <div className="caption mb-3 text-[#737373]">UNASSIGNED · {counts.unassigned}</div>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {buckets.unassigned.map((r) => <BoarderCard key={r.id} row={r} />)}
                    </div>
                </div>
            )}
        </div>
    );
}

function Column({ roleKey, rows, count }) {
    const meta = ROLE_META[roleKey];
    const Icon = meta.Icon;
    return (
        <section
            data-testid={`boarders-column-${roleKey}`}
            className={`flex flex-col rounded-md border p-5 ${meta.accent}`}
        >
            <header className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Icon size={18} className="text-[var(--brand-accent)]" />
                    <div>
                        <div className="font-serif text-xl text-[#FAFAFA]">{meta.label}</div>
                        <div className="caption text-[#737373]">{count ?? 0} in queue</div>
                    </div>
                </div>
            </header>
            <div className="flex flex-col gap-3">
                {rows.length === 0 ? (
                    <div className="rounded-sm border border-dashed border-white/10 px-4 py-6 text-center text-xs text-[#737373]">
                        No boarders match the current filter.
                    </div>
                ) : (
                    rows.map((r) => <BoarderCard key={r.id} row={r} />)
                )}
            </div>
        </section>
    );
}

function BoarderCard({ row }) {
    const covenantOk = !!row.covenant_accepted;
    const boarded = !!row.boarded_at;
    const cleared = !!row.mission_intake_completed_at;
    return (
        <article
            data-testid={`boarder-card-${row.id}`}
            className="rounded-sm border border-white/10 bg-[#141414]/60 p-4 transition-all hover:border-[var(--brand-accent)]/40"
        >
            <div className="flex items-baseline justify-between gap-3">
                <div className="font-medium text-[#FAFAFA]">{row.name || row.email}</div>
                <span className="caption text-[#737373]">
                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}
                </span>
            </div>
            <div className="mt-1 truncate text-xs text-[#A3A3A3]">{row.email}</div>

            <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
                <BondBadge ok={covenantOk} okLabel="COVENANT" pendLabel="NO COVENANT" />
                <BondBadge ok={boarded} okLabel="BOARDED" pendLabel="UNBOARDED" />
                <BondBadge ok={cleared} okLabel={`CLEARED · ${row.clearance || "Privateer"}`} pendLabel="NO INTAKE" />
                {row.specialized_role === "admiral" && (
                    <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-amber-300">
                        RES {row.residual_pct}%
                    </span>
                )}
            </div>

            {row.requested_subdomain && (
                <div className="mt-2 font-mono text-[11px] text-[#737373]">
                    {row.requested_subdomain}.bizboogie.com
                </div>
            )}
        </article>
    );
}

function BondBadge({ ok, okLabel, pendLabel }) {
    if (ok) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/10 px-2 py-0.5 text-emerald-300">
                <ShieldCheck size={10} /> {okLabel}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.04] px-2 py-0.5 text-[#737373]">
            <ShieldAlert size={10} /> {pendLabel}
        </span>
    );
}
